import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { NewDashboardHeader } from '../../components/NewDashboardHeader'; 
import { Footer } from '../../components/Footer'; 
import { parseCurrency } from '../../utils/formatters';
import { useBrokerPropertiesData } from '../../hooks/useBrokerPropertiesData';
import { useBrokerFilterOptions } from '../../hooks/useBrokerFilterOptions';
import { BrokerSearchAndFilterSection } from '../../components/broker/BrokerSearchAndFilterSection';
import { BrokerResultsContainer } from '../../components/broker/BrokerResultsContainer';
import { DashboardViewProps } from '../../config/routeMappings';
import { useAiSearch } from '../../hooks/useAiSearch';
import { resolveFirstLocationMatch } from '../../utils/locationResolver';
import { ReferralBanner } from '../../components/broker/ReferralBanner';
import { ClientPromoBanner } from '../../components/client/banner/ClientPromoBanner';
import { UserRole } from '../../types';

declare var turf: any; 

export const BrokerDashboardView: React.FC<DashboardViewProps> = ({ onLogout, ai, appUser, preselectedCodes, newlyAddedRange, forceOnlyRecent, onConsumeForceOnlyRecent }) => {
  const isClientRole = appUser?.role === UserRole.CLIENTE;
  const [searchMode, setSearchMode] = useState<'ai' | 'code' | 'map'>('ai');
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  
  const { 
    isAiSearching, 
    aiSearchError, 
    rawAiResponse, 
    isAiConfirmationOpen, 
    executeAiSearch, 
    closeAiConfirmation,
    originalQuery
  } = useAiSearch();
  
  const [filtersState, setFiltersState] = useState({ propertyCode: '', selectedUbicacion: [] as string[], selectedPropertyType: [] as string[], selectedEstrategias: [], selectedOwner: [], selectedRooms: [], selectedBaths: [], selectedGarages: [], priceRange: { min: '', max: '' }, areaRange: { min: '', max: '' }, selectedEstrato: [], antiquityRange: { min: '', max: '' }, withElevator: false, onlyRecent: false });
  
  const [appliedFilters, setAppliedFilters] = useState({ propertyCode: '', ubicacion: [] as string[], propertyType: [] as string[], estrategias: [], owner: [], rooms: [], baths: [], garages: [], priceRange: { min: null, max: null }, areaRange: { min: null, max: null }, estrato: [], antiquityRange: { min: null, max: null }, withElevator: false, onlyRecent: false });
  
  const [spatialFilter, setSpatialFilter] = useState<any | null>(null);
  const [mapSearchDisplayName, setMapSearchDisplayName] = useState<string | null>(null);

  useEffect(() => {
    if (appUser && appUser.role !== UserRole.CLIENTE) {
      setFiltersState((prev) => {
        if (prev.selectedUbicacion.length === 1 && prev.selectedUbicacion[0] === 'BOGOTÁ, D.C.') {
          return { ...prev, selectedUbicacion: [] };
        }
        return prev;
      });
      setAppliedFilters((prev) => {
        if (prev.ubicacion.length === 1 && prev.ubicacion[0] === 'BOGOTÁ, D.C.') {
          return { ...prev, ubicacion: [] };
        }
        return prev;
      });
    }
  }, [appUser?.role]);

  const dynamicFilters = useBrokerFilterOptions(appUser, [], []);
  const memoizedSortConfigs = useMemo(() => [{ key: 'fechaAgregadoNuevo' as const, direction: 'desc' as const }], []);

  const propertiesFilterInput = useMemo(() => ({ 
    propertyCodeFilter: appliedFilters.propertyCode,
    ubicacionFilter: appliedFilters.ubicacion,
    propertyTypeFilter: appliedFilters.propertyType,
    estrategiaComercialFilter: appliedFilters.estrategias,
    ownerFilter: appliedFilters.owner,
    roomsFilter: appliedFilters.rooms,
    bathsFilter: appliedFilters.baths,
    garagesFilter: appliedFilters.garages,
    priceFilter: appliedFilters.priceRange,
    areaFilter: appliedFilters.areaRange,
    estratoFilter: appliedFilters.estrato,
    antiquityFilter: appliedFilters.antiquityRange,
    withElevatorFilter: appliedFilters.withElevator,
    onlyRecentFilter: appliedFilters.onlyRecent,
    sortConfigs: memoizedSortConfigs,  
    spatialFilter, 
    appUser, 
    customSortOrders: {}, 
    newlyAddedRange, 
    searchMode, 
    viewMode,
    isSelectionMode,
    inmobiliariasConfig: dynamicFilters.inmobiliariasConfig
  }), [
    appliedFilters, memoizedSortConfigs, spatialFilter, appUser,
    newlyAddedRange, searchMode, viewMode, isSelectionMode, dynamicFilters.inmobiliariasConfig
  ]);

  const propertiesData = useBrokerPropertiesData(propertiesFilterInput);
  
  const handleApplyManualFilters = useCallback((overrides?: any) => {
    const state = overrides || filtersState;
    setAppliedFilters({ 
        propertyCode: state.propertyCode || '', 
        ubicacion: state.selectedUbicacion, 
        propertyType: state.selectedPropertyType, 
        estrategias: state.selectedEstrategias,
        owner: state.selectedOwner, 
        priceRange: { 
            min: typeof state.priceRange.min === 'string' ? parseCurrency(state.priceRange.min) : state.priceRange.min, 
            max: typeof state.priceRange.max === 'string' ? parseCurrency(state.priceRange.max) : state.priceRange.max 
        }, 
        areaRange: { 
            min: state.areaRange.min !== '' ? Number(state.areaRange.min) : null, 
            max: state.areaRange.max !== '' ? Number(state.areaRange.max) : null 
        }, 
        rooms: state.selectedRooms, 
        baths: state.selectedBaths, 
        garages: state.selectedGarages, 
        estrato: state.selectedEstrato, 
        antiquityRange: {
            min: state.antiquityRange.min !== '' ? Number(state.antiquityRange.min) : null,
            max: state.antiquityRange.max !== '' ? Number(state.antiquityRange.max) : null
        },
        withElevator: state.withElevator,
        onlyRecent: state.onlyRecent
    });
    propertiesData.handlePageChange(1);
  }, [filtersState, propertiesData]);

  const interpretedAiFiltersForModal = useMemo(() => {
    if (!rawAiResponse) return null;
    return {
        propertyTypes: rawAiResponse.tipoinmueble || [],
        locations: rawAiResponse.ubicacion || [],
        rooms: rawAiResponse.num_habitaciones || [],
        baths: rawAiResponse.num_banos || [],
        garages: rawAiResponse.num_parqueaderos || [],
        priceRange: { min: rawAiResponse.precio_min, max: rawAiResponse.precio_max },
        areaRange: { min: rawAiResponse.area_min, max: rawAiResponse.area_max },
        estrato: rawAiResponse.estrato || [],
        withElevator: !!rawAiResponse.con_ascensor
    };
  }, [rawAiResponse]);

  // Auto-apply AI filters when response is received
  useEffect(() => {
    if (rawAiResponse && isAiConfirmationOpen) {
      const interpreted = {
        propertyTypes: rawAiResponse.tipoinmueble || [],
        locations: rawAiResponse.ubicacion || [],
        rooms: rawAiResponse.num_habitaciones || [],
        baths: rawAiResponse.num_banos || [],
        garages: rawAiResponse.num_parqueaderos || [],
        priceRange: { min: rawAiResponse.precio_min, max: rawAiResponse.precio_max },
        areaRange: { min: rawAiResponse.area_min, max: rawAiResponse.area_max },
        estrato: rawAiResponse.estrato || [],
        withElevator: !!rawAiResponse.con_ascensor
      };
      handleConfirmAiFilters(interpreted);
    }
  }, [rawAiResponse, isAiConfirmationOpen]);

  const handleConfirmAiFilters = (updatedFilters: any) => {
    const resolvedLocations: string[] = [];
    if (updatedFilters.locations && updatedFilters.locations.length > 0) {
        updatedFilters.locations.forEach((locText: string) => {
            const match = resolveFirstLocationMatch(locText, dynamicFilters.allLocations);
            if (match) resolvedLocations.push(match);
        });
    }

    const nextFiltersState = {
        ...filtersState,
        selectedUbicacion: resolvedLocations,
        selectedPropertyType: updatedFilters.propertyTypes || [],
        selectedRooms: updatedFilters.rooms || [],
        selectedBaths: updatedFilters.baths || [],
        selectedGarages: updatedFilters.garages || [],
        priceRange: {
            min: updatedFilters.priceRange.min?.toString() || '',
            max: updatedFilters.priceRange.max?.toString() || ''
        },
        areaRange: {
            min: updatedFilters.areaRange.min?.toString() || '',
            max: updatedFilters.areaRange.max?.toString() || ''
        },
        selectedEstrato: updatedFilters.estrato || [],
        withElevator: !!updatedFilters.withElevator
    };

    setFiltersState(nextFiltersState);
    closeAiConfirmation();
    handleApplyManualFilters(nextFiltersState);
  };

  const onFilterChange = useCallback((field: string, value: any, applyImmediately?: boolean) => {
    setFiltersState(prev => {
        const nextState = { ...prev, [field]: value };
        if (applyImmediately) handleApplyManualFilters(nextState);
        return nextState;
    });
  }, [handleApplyManualFilters]);

  // Handle preselected codes from URL (selection links)
  useEffect(() => {
    if (preselectedCodes) {
      setSearchMode('code');
      const nextFiltersState = {
        ...filtersState,
        propertyCode: preselectedCodes
      };
      setFiltersState(nextFiltersState);
      handleApplyManualFilters(nextFiltersState);
      
      // Clean up the URL hash to avoid re-triggering on refresh
      if (window.location.hash.startsWith('#/selection/')) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  }, [preselectedCodes]); // Only run when preselectedCodes changes

  // Handle forceOnlyRecent redirect flag
  useEffect(() => {
    if (forceOnlyRecent) {
      const nextFiltersState = {
        ...filtersState,
        onlyRecent: true
      };
      setFiltersState(nextFiltersState);
      handleApplyManualFilters(nextFiltersState);
      if (onConsumeForceOnlyRecent) {
        onConsumeForceOnlyRecent();
      }
    }
  }, [forceOnlyRecent, onConsumeForceOnlyRecent]);

  const handleModeChange = (mode: 'ai' | 'code' | 'map') => {
      setSearchMode(mode); 
      setSpatialFilter(null); 
      setMapSearchDisplayName(null);
      const reset = { propertyCode: '', ubicacion: [], propertyType: [], estrategias: [], owner: [], rooms: [], baths: [], garages: [], priceRange: { min: null, max: null }, areaRange: { min: null, max: null }, estrato: [], antiquityRange: { min: null, max: null }, withElevator: false, onlyRecent: false };
      setAppliedFilters(reset); 
      setFiltersState({ ...reset, selectedUbicacion: [], selectedPropertyType: [], selectedEstrategias: [], selectedOwner: [], selectedRooms: [], selectedBaths: [], selectedGarages: [], priceRange: { min: '', max: '' }, areaRange: { min: '', max: '' }, selectedEstrato: [], antiquityRange: { min: '', max: '' } });
      
      setViewMode(mode === 'map' ? 'map' : 'grid'); 
      
      propertiesData.handlePageChange(1);
  };

  const isBrokerRole = appUser && (
      appUser.role === UserRole.BROKER || 
      appUser.role === UserRole.BROKER_INTERNO || 
      appUser.role === UserRole.BROKER_PREMIUM || 
      appUser.role === UserRole.BROKER_EXTERIOR || 
      appUser.role === UserRole.BROKERS_LEADS
  );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {!isSelectionMode && <NewDashboardHeader onLogout={onLogout} appUser={appUser} />}
      <main className="flex-grow flex flex-col max-w-[1700px] mx-auto space-y-6 px-3 sm:px-6 lg:p-8 w-full pb-32">
        {!isSelectionMode && (
            <>
                {newlyAddedRange && <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#00234F]">Novedades</h1>}
                {isClientRole && <ClientPromoBanner />}
                <BrokerSearchAndFilterSection 
                    filters={filtersState} 
                    onFilterChange={onFilterChange} 
                    dynamicFilterOptions={dynamicFilters} 
                    onApplyFilters={() => handleApplyManualFilters()} 
                    onClearFilters={() => handleModeChange(searchMode)} 
                    appUser={appUser} 
                    aiSearchQuery={aiSearchQuery} 
                    setAiSearchQuery={setAiSearchQuery} 
                    isAiSearching={isAiSearching} 
                    onAiSearch={executeAiSearch} 
                    onCodeSearch={c => { setSpatialFilter(null); setAppliedFilters(p => ({...p, propertyCode: c})); propertiesData.handlePageChange(1); }} 
                    onLocationSelect={(lat, lon, d, g) => { setMapSearchDisplayName(d); setViewMode('map'); setSpatialFilter({ center: { lat, lon }, radius: 600, geometry: turf.circle([lon, lat], 0.6, {units:'kilometers'}).geometry }); }} 
                    mapSearchDisplayName={mapSearchDisplayName} 
                    spatialFilter={spatialFilter} 
                    onClearMapSearch={() => { setSpatialFilter(null); setMapSearchDisplayName(null); }}
                    onClearSpatialFilter={() => { setSpatialFilter(null); setMapSearchDisplayName(null); }} 
                    aiSearchError={aiSearchError} 
                    searchMode={searchMode} 
                    setSearchMode={handleModeChange} 
                />
            </>
        )}
        
        <BrokerResultsContainer 
            propertiesData={propertiesData}
            appUser={appUser}
            searchMode={searchMode}
            spatialFilter={spatialFilter}
            onSpatialFilterChange={setSpatialFilter}
            isSelectionMode={isSelectionMode}
            setIsSelectionMode={setIsSelectionMode}
            inmobiliariasConfig={dynamicFilters.inmobiliariasConfig}
            viewMode={viewMode}
            setViewMode={setViewMode}
            filterInput={propertiesFilterInput}
        />
      </main>
      
      {!isSelectionMode && isBrokerRole && <ReferralBanner />}
      <Footer />
    </div>
  );
};
